import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { unreachable } from '@chainflip/utils/assertion';
import { brokerAliasMap, smartContractAliasMap } from '@chainflip/utils/consts';
import { abbreviate } from '@chainflip/utils/string';
import classNames from 'classnames';
import { Addresses } from '@/shared/components/Addresses';
import { Link } from '@/shared/components/atoms/Link';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import Pill from '@/shared/components/flip-ui-kit/Pill';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { CcmPill, LiquidationPill, OnChainPill } from '@/shared/components/pills';
import { CompactSwapRoute } from '@/shared/components/swaps/SwapRouteV2';
import { SwapStatus } from '@/shared/components/swaps/SwapStatusV2';
import {
  type GetAllSwapsQuery,
  type SwapRequestOverviewFragment,
} from '@/shared/graphql/generated/reporting/graphql';
import { type PaginationControls } from '@/shared/hooks/useRemotePagination';
import useScreenSize from '@/shared/hooks/useScreenSize';
import ChunksIcon from '@/shared/icons/flip-ui-kit/small/Chunks';
import ClockIcon from '@/shared/icons/flip-ui-kit/small/Clock';
import TimeBoost from '@/shared/icons/flip-ui-kit/small/TimeBoost';
import VaultIcon from '@/shared/icons/flip-ui-kit/small/Vault';
import { buildExplorerLink } from '@/shared/utils/chainflip';
import { tableRowAnimationProps } from '@/shared/utils/consts';
import { formatToApproxTime } from '@/shared/utils/date';
import { formatUsdValue } from '@/shared/utils/numbers';

export const swapRowColumns = [
  'Swap Details',
  'Addresses',
  'Source',
  'Status',
  'Commission',
  'Age',
] as const;

const SwapRow = ({
  swapRequest,
  isMobile,
  columns,
  app = 'explorer',
  showOracleDelta = false,
}: {
  swapRequest: SwapRequestOverviewFragment;
  isMobile: boolean;
  columns: (typeof swapRowColumns)[number][];
  app: 'explorer' | 'lp';
  showOracleDelta?: boolean;
}) => {
  const router = useRouter();
  const swapDetailsUrl = buildExplorerLink('Chainflip', 'swap', swapRequest.swapRequestNativeId);
  const destinationAddress = swapRequest.destinationAddress ?? '';
  const refundAddress = swapRequest.refundAddress ?? '';
  const executedChunks = swapRequest.executedChunks ?? 0;
  const totalChunks = swapRequest.totalChunks ?? 0;

  const smartContractAlias =
    smartContractAliasMap[destinationAddress.toLowerCase() as keyof typeof smartContractAliasMap] ||
    (refundAddress
      ? smartContractAliasMap[refundAddress.toLowerCase() as keyof typeof smartContractAliasMap]
      : undefined);

  const { isBoosted, mainBrokerAccountSs58Id, status, isOnChain, isLiquidation, liquidationType } =
    swapRequest;

  let pillContent;

  if (smartContractAlias) {
    pillContent = <Pill color="neutral" text={smartContractAlias} />;
  } else if (mainBrokerAccountSs58Id) {
    const name =
      swapRequest.broker?.alias ??
      brokerAliasMap[mainBrokerAccountSs58Id]?.name ??
      abbreviate(mainBrokerAccountSs58Id);
    pillContent = (
      <Pill
        color="neutral"
        text={name}
        onClick={(e) => {
          e.stopPropagation();
          router.push(`/brokers/${mainBrokerAccountSs58Id}`);
        }}
      />
    );
  } else if (isOnChain) {
    pillContent = <OnChainPill />;
  } else if (isLiquidation) {
    pillContent = <LiquidationPill liquidationType={liquidationType} />;
  } else {
    pillContent = <Pill color="neutral" text="Vault swap" Icon={VaultIcon} />;
  }

  if (isMobile) {
    return (
      <ClickableTableRow
        {...tableRowAnimationProps}
        href={swapDetailsUrl}
        className={classNames(
          app === 'lp' ? 'h-[72px]' : 'h-[91px]',
          'text-xs',
          swapRequest.isInProgress && 'bg-cf-gray-4 hover:bg-[#303030] [&>td]:!border-cf-gray-5',
        )}
      >
        <td>
          <div className="flex flex-col gap-y-2">
            <div className="flex justify-between">
              <SwapStatus status={status} />
              <div className="flex gap-x-1 text-cf-light-2">
                {isBoosted ? <TimeBoost className="text-cf-pink-1" /> : <ClockIcon />}
                {swapRequest.startedBlockTimestamp && (
                  <AgeWithTooltip timestamp={swapRequest.startedBlockTimestamp} />
                )}
              </div>
            </div>
            <div className="flex justify-between">
              <CompactSwapRoute
                routeInfo={{
                  destinationAsset: swapRequest.destAsset,
                  sourceAsset: swapRequest.sourceAsset,
                }}
                swapRequest={swapRequest}
                status={status}
                showIntermediate={false}
                showOracleDelta={showOracleDelta}
              />
            </div>
          </div>
        </td>
      </ClickableTableRow>
    );
  }

  return (
    <ClickableTableRow
      {...tableRowAnimationProps}
      href={swapDetailsUrl}
      className={classNames(
        app === 'lp' ? 'h-[72px]' : 'h-[91px]',
        'text-xs',
        swapRequest.isInProgress && 'bg-cf-gray-4 hover:bg-[#303030] [&>td]:!border-cf-gray-5',
      )}
    >
      {columns.map((columnName) => {
        switch (columnName) {
          case 'Swap Details':
            return (
              <td key={columnName}>
                <div className="flex gap-x-4 whitespace-nowrap">
                  <div
                    className={classNames(
                      'flex w-[65px] flex-col gap-y-1',
                      swapRequest.isCcm ? 'items-center' : 'justify-center',
                    )}
                  >
                    <Link href={swapDetailsUrl} underline noPropagate className="text-center">
                      #{swapRequest.swapRequestNativeId}
                    </Link>
                    {swapRequest.isCcm && <CcmPill />}
                  </div>
                  <div className="inline-flex">
                    <CompactSwapRoute
                      routeInfo={{
                        destinationAsset: swapRequest.destAsset,
                        sourceAsset: swapRequest.sourceAsset,
                      }}
                      swapRequest={swapRequest}
                      status={status}
                      showOracleDelta={showOracleDelta}
                    />
                  </div>
                </div>
              </td>
            );

          case 'Addresses':
            return (
              <td key={columnName}>
                <Addresses swap={swapRequest} />
              </td>
            );

          case 'Source':
            return <td key={columnName}>{pillContent}</td>;

          case 'Status':
            return (
              <td key={columnName} className="min-w-[140px]">
                <div className="flex flex-col gap-y-1 font-aeonikMedium">
                  <SwapStatus status={status} />
                  {totalChunks > 1 && (
                    <div className="flex gap-x-1">
                      <ChunksIcon className="text-cf-light-3" />
                      {executedChunks}/{totalChunks}
                    </div>
                  )}
                </div>
              </td>
            );

          case 'Age':
            return (
              <td
                key={columnName}
                className="whitespace-nowrap text-right text-cf-light-2"
                title={
                  swapRequest.startedBlockTimestamp
                    ? new Date(swapRequest.startedBlockTimestamp).toLocaleString()
                    : 'A few seconds ago'
                }
              >
                <div className="flex flex-col justify-end gap-y-1">
                  {swapRequest.startedBlockTimestamp && (
                    <AgeWithTooltip timestamp={swapRequest.startedBlockTimestamp} />
                  )}
                  {status === 'SUCCESS' && swapRequest.completedInSeconds && (
                    <div className="flex items-center justify-end gap-x-1 text-12">
                      {swapRequest.isBoosted ? (
                        <TimeBoost className="text-cf-pink-1" />
                      ) : (
                        <ClockIcon />
                      )}
                      Took&nbsp;
                      {formatToApproxTime(swapRequest.completedInSeconds)}
                    </div>
                  )}
                </div>
              </td>
            );

          case 'Commission':
            return (
              <td key={columnName} className="whitespace-nowrap text-left text-cf-light-2">
                <div className="flex flex-col justify-end gap-y-1">
                  {swapRequest.mainBrokerFeeValueUsd && (
                    <div className="text-cf-light-3">
                      {formatUsdValue(swapRequest.mainBrokerFeeValueUsd)}
                    </div>
                  )}
                </div>
              </td>
            );

          default:
            return unreachable(columnName);
        }
      })}
    </ClickableTableRow>
  );
};

export const LatestSwapsTable = ({
  swapRequests,
  columns,
  isLoading,
  paginationControls,
  preview,
  app = 'explorer',
  showOracleDelta = false,
}: {
  swapRequests: GetAllSwapsQuery | null;
  columns: (typeof swapRowColumns)[number][];
  isLoading: boolean;
  paginationControls: PaginationControls;
  preview?: boolean;
  app?: 'explorer' | 'lp';
  showOracleDelta?: boolean;
}) => {
  const { isMobile } = useScreenSize();
  const swaps = swapRequests?.allSwapRequests?.edges ?? [];

  const swapTableRows = useMemo(
    () =>
      swaps
        .slice(0, preview ? 10 : swaps.length)
        .map(({ node }) => (
          <SwapRow
            key={node.id}
            swapRequest={node}
            isMobile={isMobile}
            columns={columns}
            app={app}
            showOracleDelta={showOracleDelta}
          />
        )),
    [swapRequests, isMobile, showOracleDelta],
  );

  return (
    <TableV2
      isLoading={isLoading}
      rows={swapTableRows}
      className="rounded-t-none"
      emptyRowsContent="No swaps here yet"
      columns={
        isMobile ? [''] : columns.map((c) => (c === 'Age' ? { name: c, alignment: 'right' } : c))
      }
      tbodyClassName={classNames(isMobile ? '[&>tr>td]:p-3' : '[&>tr>td]:p-4')}
      hideHeaders={isMobile}
      {...(!preview && { paginationControls })}
    />
  );
};
